import { ActionButton, ActionButtonType } from "@octopusdeploy/design-system-components";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { HostingEnvironment } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ArgoCDInstanceConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDInstanceConfiguration";
import { ConfigurationView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/ConfigurationView";
import type { FoundGatewayInstance } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/InstallationView";
import { InstallationView } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/InstallationView";
import { ConfigurationViewHelpContent, ConfigurationViewHelpImage, InstallationViewHelpContent, InstallationViewHelpImage } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/components/AddDialogHelp";
import { validateConfiguration } from "~/areas/infrastructure/components/ArgoCDInstances/configurationValidation";
import { useBearerToken } from "~/areas/infrastructure/components/ArgoCDInstances/useBearerToken";
import { useValidatingModel } from "~/areas/infrastructure/components/ArgoCDInstances/useValidatingModel";
import { getServerGrpcUri } from "~/areas/infrastructure/components/MachineSettings/Endpoints/KubernetesAgent/ConfigurationPage";
import { client } from "~/clientInstance";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import type { PagedOnboardingDialogPageData } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { PagedOnboardingDialog } from "~/components/OnboardingDialog/PagedOnboardingDialog";
type AddArgoCDInstanceDialogProps = {
    open: boolean;
    onClose: (wasInstalled: boolean) => void;
    spaceId: string;
};
export const AddArgoCDInstanceDialog = (props: AddArgoCDInstanceDialogProps) => {
    const [foundGateway, setFoundGateway] = useState<FoundGatewayInstance>();
    const isGatewayFound = !!foundGateway;
    const bearerToken = useBearerToken();
    const { navigate } = useSpaceAwareNavigation();
    const { model, setModel, validate, getFieldError, reset } = useValidatingModel<ArgoCDInstanceConfiguration>({
        initialModel: {
            name: "",
            argoCDUrl: "argocd-server.argocd.svc.cluster.local",
            argoCDAuthenticationToken: "",
            environmentIds: [],
            environmentSlugs: [],
            octopusServerUrl: client.resolve("/"),
            octopusGrpcUrl: getServerGrpcUri(),
            spaceId: props.spaceId,
        },
        onValidate: async (repo, modelToValidate) => await validateConfiguration(repo, modelToValidate),
    });
    useEffect(() => {
        if (props.open) {
            reset();
        }
    }, [props.open, reset]);
    //used to allow modification of URL's
    const { result: licenseResult } = useInlineStatusQuery((repo) => repo.Licenses.getCurrentStatus(), [], "Get current license status");
    const isSelfHosted = useMemo(() => licenseResult?.HostingEnvironment === HostingEnvironment.SelfHosted, [licenseResult]);
    const canRegenerateToken = bearerToken.hasExpired && !isGatewayFound;
    const onClose = useCallback(() => {
        props.onClose(isGatewayFound);
    }, [isGatewayFound, props]);
    const navigateToGenerateAnnotations = useCallback(() => {
        if (!foundGateway) {
            return;
        }
        const link = links.argoCDInstanceSettingsPage.generateUrl({
            spaceId: foundGateway.SpaceId,
            instanceId: foundGateway.InstanceId,
        }, { openAnnotationsDrawer: true });
        navigate(link.resolveWithSpaceId(foundGateway.SpaceId));
    }, [foundGateway, navigate]);
    const installationPrimaryButton = useMemo(() => {
        if (canRegenerateToken) {
            return <ActionButton label="Regenerate token" onClick={() => bearerToken.refresh()} type={ActionButtonType.Primary}/>;
        }
        if (isGatewayFound) {
            return <ActionButton label="Generate scoping annotations" onClick={() => navigateToGenerateAnnotations()} type={ActionButtonType.Primary}/>;
        }
    }, [bearerToken, canRegenerateToken, isGatewayFound, navigateToGenerateAnnotations]);
    const pages: PagedOnboardingDialogPageData[] = useMemo(() => [
        {
            name: "Register Argo CD Instance",
            title: "Register Argo CD Instance",
            isPageModelValid: async () => await validate(),
            onMovingToNextPage: async () => bearerToken.refresh(),
            helpPanelContent: ConfigurationViewHelpContent,
            helpPanelImage: ConfigurationViewHelpImage,
            render: () => <ConfigurationView model={model} setModel={setModel} getFieldError={getFieldError} isSelfHosted={isSelfHosted}/>,
        },
        {
            name: "Install Argo CD Gateway",
            title: "Install Argo CD Gateway",
            helpPanelContent: InstallationViewHelpContent,
            helpPanelImage: InstallationViewHelpImage,
            render: () => <InstallationView configuration={model} bearerToken={bearerToken} onGatewayFound={(gateway) => setFoundGateway(gateway)}/>,
            customPrimaryButton: installationPrimaryButton,
            //stops the cancel button from showing when the gateway is found
            alwaysShowCloseButton: true,
        },
    ], [bearerToken, getFieldError, installationPrimaryButton, isSelfHosted, model, setModel, validate]);
    return (<PagedOnboardingDialog open={props.open} close={onClose} name="Add Argo CD Instance" showPageIndicator={true} contentPanelHeight={600} pages={pages} hidePreviousButton={isGatewayFound} hideCompleteButton={false} helpPanelTheme={"light"}/>);
};
